/**
 * ChatInput — message composition bar.
 *
 * Handles: text entry, send button, typing indicator emission,
 * edit mode (pre-fills content when editing a message),
 * file uploads with drag-and-drop support.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { sendTypingStart, sendTypingStop } from '../../api/chatConnection';
import { uploadFile } from '../../api/fileStorage';
import type { UploadResult } from '../../api/fileStorage';
import FileUploadButton from './FileUploadButton';
import type { FileUpload } from './FileUploadButton';
import styles from './Chat.module.css';

interface ChatInputProps {
  conversationId: string;
  onSend: (content: string, attachmentFileKeys?: string[]) => void;
  /** When set, input switches to edit mode */
  editingContent?: string | null;
  onCancelEdit?: () => void;
  disabled?: boolean;
}

export default function ChatInput({
  conversationId,
  onSend,
  editingContent,
  onCancelEdit,
  disabled = false,
}: ChatInputProps) {
  const { t } = useAppTranslation('chat');
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingRef = useRef(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dragDropRef = useRef<HTMLDivElement>(null);

  // When entering edit mode, populate the input
  useEffect(() => {
    if (editingContent != null) {
      setText(editingContent);
      inputRef.current?.focus();
    }
  }, [editingContent]);

  const handleTyping = useCallback(() => {
    if (!typingRef.current) {
      typingRef.current = true;
      sendTypingStart(conversationId);
    }
    // Reset the "stop typing" timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      typingRef.current = false;
      sendTypingStop(conversationId);
    }, 2000);
  }, [conversationId]);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    const hasAttachments = attachments.length > 0;
    if (!trimmed && !hasAttachments) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Upload files if any
      const fileKeys: string[] = [];
      if (hasAttachments) {
        for (const attachment of attachments) {
          if (attachment.error) {
            setUploadError(attachment.error);
            setUploading(false);
            return;
          }
          try {
            const result = await uploadFile(
              attachment.file,
              'chat',
              conversationId,
              attachment.file.name
            );
            fileKeys.push(result.key);
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : 'Upload failed';
            setUploadError(`Failed to upload ${attachment.file.name}: ${errMsg}`);
            setUploading(false);
            return;
          }
        }
      }

      // Send message with attachments
      onSend(trimmed, fileKeys);
      setText('');
      setAttachments([]);

      // Stop typing indicator
      if (typingRef.current) {
        typingRef.current = false;
        sendTypingStop(conversationId);
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      inputRef.current?.focus();
    } finally {
      setUploading(false);
    }
  }, [text, attachments, onSend, conversationId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === 'Escape' && editingContent != null) {
        onCancelEdit?.();
        setText('');
      }
    },
    [handleSend, editingContent, onCancelEdit]
  );

  return (
    <div className={styles.chatInputContainer}>
      {/* Edit mode indicator */}
      {editingContent != null && (
        <div className={styles.editBanner}>
          <span>{t('actions.edit')}</span>
          <button type="button" className={styles.editCancelBtn} onClick={() => { onCancelEdit?.(); setText(''); }}>
            ✕
          </button>
        </div>
      )}

      {/* Attachment preview */}
      {attachments.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--bg-tertiary)',
            borderBlockEnd: '1px solid var(--border-default)',
            maxHeight: 120,
            overflowY: 'auto',
          }}
        >
          {attachments.map((att, idx) => (
            <div
              key={`${att.file.name}-${idx}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px',
                marginBlockEnd: 4,
                background: 'var(--bg-surface)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <span style={{ fontSize: 14 }}>
                {att.file.type.startsWith('image/') && '📸'}
                {att.file.type === 'application/pdf' && '📄'}
                {att.file.type.startsWith('video/') && '🎥'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {att.file.name}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                  {(att.file.size / 1024 / 1024).toFixed(1)} MB
                </div>
              </div>
              {att.error && (
                <div style={{ fontSize: 12, color: '#ef4444' }}>⚠️</div>
              )}
              <button
                type="button"
                onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                style={{
                  padding: '2px 6px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: 14,
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div
          style={{
            padding: '8px 12px',
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            fontSize: 12,
            borderBlockEnd: '1px solid var(--border-default)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {uploadError}
          <button
            type="button"
            onClick={() => setUploadError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444',
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div className={styles.chatInputRow}>
        <FileUploadButton
          conversationId={conversationId}
          onFilesSelected={(files) => setAttachments(files)}
          disabled={disabled || uploading}
        />

        <textarea
          ref={inputRef}
          className={styles.chatTextarea}
          placeholder={t('conversation.typeMessage')}
          value={text}
          onChange={(e) => { setText(e.target.value); handleTyping(); }}
          onKeyDown={handleKeyDown}
          disabled={disabled || uploading}
          rows={1}
        />
        <button
          type="button"
          className={styles.sendButton}
          onClick={handleSend}
          disabled={disabled || uploading || (!text.trim() && attachments.length === 0)}
          aria-label={t('conversation.send')}
        >
          {uploading ? '⏳' : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
